'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [credits, setCredits] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      setProfile(data);
    } catch {}
  };

  const fetchCredits = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('credit_balances')
        .select('*')
        .eq('user_id', userId)
        .single();
      if (data) {
        // Check if daily credits need reset (new day)
        const lastReset = new Date(data.last_daily_reset);
        const now = new Date();
        const isNewDay =
          lastReset.getUTCFullYear() !== now.getUTCFullYear() ||
          lastReset.getUTCMonth() !== now.getUTCMonth() ||
          lastReset.getUTCDate() !== now.getUTCDate();
        if (isNewDay) {
          await supabase.rpc('reset_daily_credits', { p_user_id: userId });
          const { data: refreshed } = await supabase
            .from('credit_balances')
            .select('*')
            .eq('user_id', userId)
            .single();
          setCredits(refreshed);
        } else {
          setCredits(data);
        }
      }
    } catch {}
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchCredits(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchCredits(session.user.id);
      } else {
        setProfile(null);
        setCredits(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.displayName || '',
          username: metadata?.username || '',
          avatar_url: metadata?.avatarUrl || '',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    // Try direct email login first
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) return data;

    // If it looks like a username (no @), try to look up the email
    if (!email.includes('@')) {
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('username', email.toLowerCase())
        .single();

      if (profileData?.email) {
        const { data: data2, error: error2 } = await supabase.auth.signInWithPassword({
          email: profileData.email,
          password,
        });
        if (error2) throw error2;
        return data2;
      }
    }

    throw error;
  };

  // Google OAuth
  const signInWithGoogle = async () => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://gameforge7999.builtwithrocket.new';
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setCredits(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Refresh credits
  const refreshCredits = async () => {
    if (user) await fetchCredits(user.id);
  };

  // Refresh profile
  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    profile,
    credits,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshCredits,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
